import { BaseAdapter } from "../base.js";
import type { AdapterContext } from "../base.js";
import type { Url } from "../../data-model/primitives.js";
import type { NovelMetadata, ChapterRef, Chapter } from "../../data-model/novel.js";
import { unsafeBrandAdapterId, unsafeBrandChapterIndex, unsafeBrandUrl } from "../../data-model/primitives.js";
import type { CheerioAPI } from "cheerio";
import { parseNovelPage, parseTocPage, parseChapterPage } from "./parser.js";
import { mapHostname } from "../../utils/url.js";
import { paginateToc, type TocWalkPage } from "../shared/paginate.js";

export class TruyenFullAdapter extends BaseAdapter {
  readonly id = unsafeBrandAdapterId("truyenfull");
  readonly displayName = "TruyenFull";
  readonly hostnames = [
    "truyenfull.today",
    "www.truyenfull.today",
    "truyenfull.vision",
    "www.truyenfull.vision",
    "truyenfull.vn",
    "www.truyenfull.vn",
  ];

  async fetchMetadata(url: Url, ctx: AdapterContext): Promise<NovelMetadata> {
    const target = this.#normalize(url);
    const $page1 = await this.loadHtml(target, ctx);
    const meta = parseNovelPage($page1, target);
    const totalChapters = await this.#computeTotalChapters($page1, target, ctx);
    return totalChapters !== undefined ? { ...meta, totalChapters } : meta;
  }

  // The novel page IS page 1 of the TOC. Page sizes on truyenfull are constant
  // for pages 1..(N-1), so total = (lastPage-1) * page1Count + lastPageCount.
  // For single-page novels we can answer from page 1 alone; for multi-page we
  // pay one extra HTTP for the last page.
  async #computeTotalChapters(
    $page1: CheerioAPI,
    novelUrl: Url,
    ctx: AdapterContext,
  ): Promise<number | undefined> {
    const { refs: page1Refs, totalPages, maxLinkedPage } = parseTocPage($page1, unsafeBrandChapterIndex(0), novelUrl);
    const page1Count = page1Refs.length;
    if (page1Count === 0) return undefined;
    const lastPage = totalPages ?? maxLinkedPage ?? 1;
    if (lastPage <= 1) return page1Count;
    const base = stripTrailingSlash(this.#normalize(novelUrl));
    const lastPageUrl = unsafeBrandUrl(`${base}/trang-${lastPage}/`);
    const $last = await this.loadHtml(lastPageUrl, ctx);
    const { refs: lastRefs } = parseTocPage($last, unsafeBrandChapterIndex(0), lastPageUrl);
    if (lastRefs.length === 0) return undefined;
    return (lastPage - 1) * page1Count + lastRefs.length;
  }

  async *fetchChapterList(url: Url, ctx: AdapterContext): AsyncIterable<ChapterRef> {
    const base = stripTrailingSlash(this.#normalize(url));
    let knownTotalPages: number | undefined;
    yield* paginateToc({
      ctx,
      fetchPage: async (page, startIndex): Promise<TocWalkPage | null> => {
        const pageUrl = page === 1 ? unsafeBrandUrl(`${base}/`) : unsafeBrandUrl(`${base}/trang-${page}/`);
        const $ = await this.loadHtml(pageUrl, ctx);
        const { refs, isLastPage, totalPages } = parseTocPage($, startIndex, pageUrl);
        if (page === 1 && totalPages !== undefined) knownTotalPages = totalPages;
        return {
          refs,
          isLast: isLastPage || (knownTotalPages !== undefined && page >= knownTotalPages),
        };
      },
    });
  }

  async fetchChapter(ref: ChapterRef, ctx: AdapterContext): Promise<Chapter> {
    const target = this.#normalize(ref.url);
    const targetRef = target === ref.url ? ref : { ...ref, url: target };
    const $ = await this.loadHtml(target, ctx);
    return parseChapterPage($, targetRef);
  }

  #normalize(url: Url): Url {
    return mapHostname(url, {
      "truyenfull.vn": "truyenfull.today",
      "www.truyenfull.vn": "truyenfull.today",
      "truyenfull.vision": "truyenfull.today",
      "www.truyenfull.vision": "truyenfull.today",
      "www.truyenfull.today": "truyenfull.today",
    });
  }
}

function stripTrailingSlash(u: Url): string {
  const s = String(u);
  return s.endsWith("/") ? s.slice(0, -1) : s;
}
